import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import {
    UserRecommendationsTitle,
    type UserRecommendationsTitleAlign,
} from './UserRecommendationsTitle.tsx';

import styles from './UserRecommendations.module.css';

export type UserRecommendationsGridLayoutProps = {
    isMobile: boolean;
    isTablet: boolean;
    dataTn: string;
    title?: ReactNode;
    titleAlign: UserRecommendationsTitleAlign;
    moduleRef: React.RefObject<HTMLDivElement | null>;
    isLoading?: boolean;
    showToggleButton: boolean;
    showViewLess: boolean;
    autoLoadMore: boolean;
    hasMoreToLoad: boolean;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onLoadMore: () => void;
    children: ReactNode;
};

export const UserRecommendationsGridLayout: FC<UserRecommendationsGridLayoutProps> = ({
    isMobile,
    isTablet,
    dataTn,
    title,
    titleAlign,
    moduleRef,
    isLoading = false,
    showToggleButton,
    showViewLess,
    autoLoadMore,
    hasMoreToLoad,
    isExpanded,
    onToggleExpand,
    onLoadMore,
    children,
}) => {
    const titleContent = title ?? (
        <UserRecommendationsTitle titleAlign={titleAlign} isMobile={isMobile} />
    );

    const gapClass = isMobile ? dibsCss.gapSmaller : dibsCss.gapSmall;
    let marginTopClass = dibsCss.mtLarge;
    if (isLoading) {
        marginTopClass = dibsCss.mtMedium;
    } else if (isMobile) {
        marginTopClass = dibsCss.mtSmall;
    }

    return (
        <div
            ref={moduleRef}
            className={classnames(isMobile ? dibsCss.myLarge : dibsCss.myLarger)}
            data-tn={dataTn}
        >
            {titleContent}
            <div
                className={classnames(
                    dibsCss.grid,
                    styles.grid,
                    gapClass,
                    marginTopClass,
                    !isMobile && isTablet && styles.gridTablet,
                    !isMobile && !isTablet && styles.gridDesktop
                )}
            >
                {children}
            </div>
            {autoLoadMore && !isExpanded && hasMoreToLoad ? (
                <VisibilityTracker
                    observerOptions={{
                        rootMargin: '700px 0px',
                    }}
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            onLoadMore();
                        }
                    }}
                />
            ) : null}
            {showToggleButton ? (
                <div className={classnames(dibsCss.flex, dibsCss.justifyCenter, dibsCss.mtSmall)}>
                    <Button
                        type="secondary"
                        size="medium"
                        dataTn={`${dataTn}-toggle`}
                        onClick={onToggleExpand}
                    >
                        {showViewLess ? (
                            <FormattedMessage
                                id="UserRecommendations.viewFewer"
                                defaultMessage="View Fewer Items"
                            />
                        ) : (
                            <FormattedMessage
                                id="UserRecommendations.viewMore"
                                defaultMessage="View More Items"
                            />
                        )}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
